/**
 * @file Reusable collection checkbox selection component
 * @version 1.0.0
 * @author Claude Assistant
 * @since 2025-07-09
 */

const { ref, computed, watch } = Vue;

export default {
  name: 'CollectionSelector',

  template: `
    <div class="collection-selector">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h6 class="mb-0">Select Collections</h6>
        <div class="btn-group btn-group-sm">
          <button 
            class="btn btn-outline-secondary"
            @click="selectAll"
            :disabled="allSelected"
          >
            Select All
          </button>
          <button 
            class="btn btn-outline-secondary"
            @click="deselectAll"
            :disabled="noneSelected"
          >
            Deselect All
          </button>
        </div>
      </div>
      
      <div class="collection-list">
        <div 
          v-for="collection in collections" 
          :key="collection.name"
          class="form-check mb-2"
        >
          <input 
            :id="'collection-' + collection.name"
            class="form-check-input"
            type="checkbox"
            :value="collection.name"
            v-model="selectedCollections"
            @change="updateSelection"
          >
          <label 
            class="form-check-label d-flex justify-content-between align-items-center w-100"
            :for="'collection-' + collection.name"
          >
            <div>
              <strong>{{ collection.name }}</strong>
              <div class="text-muted small">
                {{ collection.items.length }} records
                <span v-if="collection.replicationState" class="badge bg-info ms-1">
                  <i class="bi-cloud-arrow-up"></i> Synced
                </span>
              </div>
            </div>
            <div v-if="collection.exportedAt" class="text-muted small">
              {{ formatDate(collection.exportedAt) }}
            </div>
          </label>
        </div>
      </div>
      
      <div v-if="collections.length === 0" class="text-muted text-center py-3">
        No collections available
      </div>
    </div>
  `,

  props: {
    collections: {
      type: Array,
      required: true,
      default: () => [],
    },
    modelValue: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update:modelValue'],

  setup(props, { emit }) {
    const selectedCollections = ref([...props.modelValue]);

    const allSelected = computed(
      () =>
        props.collections.length > 0 &&
        selectedCollections.value.length === props.collections.length
    );

    const noneSelected = computed(() => selectedCollections.value.length === 0);

    const selectAll = () => {
      if (props.disabled) return;
      selectedCollections.value = props.collections.map(c => c.name);
      updateSelection();
    };

    const deselectAll = () => {
      if (props.disabled) return;
      selectedCollections.value = [];
      updateSelection();
    };

    const updateSelection = () => {
      emit('update:modelValue', [...selectedCollections.value]);
    };

    const formatDate = dateString => {
      if (!dateString) return '';
      return new Date(dateString).toLocaleDateString();
    };

    // Watch for external changes to modelValue
    watch(
      () => props.modelValue,
      newValue => {
        selectedCollections.value = [...newValue];
      },
      { deep: true }
    );

    return {
      selectedCollections,
      allSelected,
      noneSelected,
      selectAll,
      deselectAll,
      updateSelection,
      formatDate,
    };
  },
};
